import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useCallback } from 'react';
import zeplinColor from 'theme/Color';
import { capitalizeStage } from '../DealsDetails.utils';
import CustomButton from 'components/shared/CustomButton/CustomButton';

export type DetailRowProps = {
  label: string;
  value: string | number | boolean | null | undefined;
  isAvatar?: boolean;
  isSelect?: boolean;
  isChip?: boolean;
  hasActivity?: boolean;
  format?: (value: any) => string;
  fieldName?: string;
  onChange?: (fieldName: string, newValue: string) => void;
  options?: { value: string; label: string }[];
  onActivityHistoryClick?: () => void;
};

export const DetailRow = ({
  label,
  value,
  isAvatar,
  isChip,
  isSelect,
  hasActivity,
  format,
  fieldName,
  onChange,
  options,
  onActivityHistoryClick,
}: DetailRowProps) => {
  const displayValue = format ? format(value) : (value === null || value === undefined ? 'N/A' : String(value));
  const isNA = displayValue === 'N/A' || !displayValue;

  const handleSelectChange = useCallback((newValue: string) => {
    if (onChange && fieldName) {
      onChange(fieldName, newValue);
    }
  }, [onChange, fieldName]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: 24, display: 'flex', color: zeplinColor.Neutral8 }}>
        <LabelOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant='body2' sx={{ width: 180, color: zeplinColor.Neutral8, flexShrink: 0}}>
        {label}
      </Typography>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ flexGrow: 1 }}>
        {isAvatar && !isNA && typeof displayValue === 'string' && (
          <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>{displayValue.charAt(0)}</Avatar>
        )}

        {isChip ? (
          <Chip
            label={displayValue}
            size='small'
            sx={{ bgcolor: 'black', color: 'white', height: 20, fontSize: '0.7rem' }}
          />
        ) : isSelect && onChange ? (
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <Select
              value={displayValue !== 'N/A' ? displayValue.toLowerCase() : ''}
              onChange={(e) => handleSelectChange(e.target.value)}
              renderValue={(selected) => {
                const selectedOption = options?.find(opt => opt.value === selected);
                let displayText = selectedOption?.label || selected || displayValue;
                if (!selectedOption && selected) {
                  displayText = capitalizeStage(selected);
                }
                return (
                  <Typography
                    component='span'
                    sx={{
                      color: zeplinColor.BasicBlack,
                      fontSize: '0.875rem',
                    }}
                  >
                    {displayText}
                  </Typography>
                );
              }}
              sx={{
                borderRadius: '50px',
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: zeplinColor.Neutral5,
                fontSize: '0.875rem',
                height: '28px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '4px 28px 4px 12px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiSelect-icon': {
                  color: zeplinColor.BasicBlack,
                  right: '8px',
                },
              }}
              IconComponent={KeyboardArrowDown}
            >
              {options && options.length > 0 ? (
                options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No options available</MenuItem>
              )}
            </Select>
          </FormControl>
        ) : (
          <Typography
            variant='body2'
            sx={{ color: isNA ? 'text.disabled' : 'text.primary' }}
          >
            {displayValue}
          </Typography>
        )}

        {hasActivity && (
          <CustomButton
            onClick={() => {
              if (onActivityHistoryClick) {
                onActivityHistoryClick();
              }
            }}
            variant='text'
            text='Activity History'
            disabled={false}
          />
        )}
      </Stack>
    </Box>
  );
};
